import { 
  Button,
  Card, 
  CardContent
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { EditSkeleton } from "components/admin/admin.overlays";
import InfoModal from "components/shared/infoModal/InfoModal";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetResidentsAllQuery } from "store/apis/ResidentsApi";
import { setGuestInfo } from "store/slices/orderSlice/OrderSlice";
import "./Guest.css";
import { useForm } from "react-hook-form";
import { useGetFacilityGuestChargesQuery } from "store/apis/OrdersApi";
import GuestOrder from "./GuestOrder";
import EmployeeOrder from "./EmployeeOrder";
import OtherOrder from "./OtherOrder";

export default function Guest({currentUserData}) {
  const [open, setOpen] = useState(false);
  const [modalText, setModalText] = useState("");
  const [isError, setIsError] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [chargesByType, setChargesByType] = useState([]); 

  const methods = useForm({
    shouldUnregister: false,
    mode: "all"
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    formState: { errors }
  } = methods;

  const {
    data: residents,
    isFetching: fetchingResidents,
    isError: errorResidents,
    error: errorResidentMsg
  } = useGetResidentsAllQuery();

  const {
    data: facilityCharges,
    isFetching: fetchingCharges,
    isSuccess: successCharges
  } = useGetFacilityGuestChargesQuery(currentUserData?.facilityId, {
    refetchOnMountOrArgChange: true, skip: !currentUserData?.facilityId
  });

  useEffect(() => {
    if (!fetchingResidents && errorResidents) {
      setIsError(true);
      setModalText(errorResidentMsg?.data?.messages?.join("\n"));
      setOpen(true);
    }
  }, [fetchingResidents, errorResidents]);

  useEffect(() => {
    if (!fetchingCharges && successCharges) {
      //Default set to Guest type on load
      const guestCharges = facilityCharges.filter((charge) => charge?.isGuest);

      setChargesByType(() => [...guestCharges]);
      setValue("guestBillingTypeId", 0); //Explicitly not set: Task 6923
    }
  }, [fetchingCharges, successCharges]);

  const residentGuestCheck = watch('selectedGuestType', 1);
  const guestBillingTypeId = watch('guestBillingTypeId');
  const employeeId = watch('employeeId');

  useEffect(() => {
    setValue('guestName', '');
    setValue('employeeId', 0)
    
    //reset selectedResident formValue when guest type is not resident guest
    if (residentGuestCheck !== 1) {
      setValue('selectedResident', 0);
      clearErrors('selectedResident'); 
    }

    // Update charges based on selected guest type
    if (facilityCharges && residentGuestCheck) {
      let filteredCharges = [];
      
      switch(residentGuestCheck) {
        case 1: // Guest
          filteredCharges = facilityCharges.filter((charge) => charge?.isGuest);
          break;
        case 2: // Employee
          filteredCharges = facilityCharges.filter((charge) => charge?.isEmployee);
          break;
        case 3: // Other
          filteredCharges = facilityCharges.filter((charge) => charge?.isOther);
          break;
        default:
          filteredCharges = [];
      }

      setChargesByType(filteredCharges);
      // Don't auto-select a charge type; require the user to choose one: Task 6923
      setValue("guestBillingTypeId", 0);
    }

  }, [residentGuestCheck, facilityCharges])

  const handleGuestSubmit = (data) => {
    if (!data?.guestName?.trim()?.length) {
      setModalText("You should enter the Guest/Employee Name.");
      setOpen(true);
    } else if (data?.selectedGuestType === 1 && data?.selectedResident === 0) {
      setModalText("Select Guest/Non-Resident Name for Guest Type");
      setOpen(true);
    } else if (!data?.guestBillingTypeId || data?.guestBillingTypeId === 0) {
      setModalText("Please select a valid charge type.");
      setOpen(true);
    } else {

      const selectedCharge = facilityCharges?.find((charge) => charge.id === data?.guestBillingTypeId);

      dispatch(setGuestInfo({
        ...data,
        selectedGuestType: residentGuestCheck,
        employeeId: employeeId ?? null,
        //only relevant for guest type orders
        residentMealPlanCharge: residentGuestCheck === 1 ? !!selectedCharge?.residentMealPlanCharge : false
      }));
      navigate("/takeorder/guest/order");
    }
  }

  const handleClose = () => {
    setIsError(false);
    setOpen(false);
  }

  return (
    <> { fetchingResidents ? (
      <EditSkeleton />
      )
      :
      (
        <Grid container justifyContent="center" alignItems="center" direction="column">
          <Card sx={{width: "98%", margin: '1%'}}>
            <CardContent>
              <Grid container justifyContent="center" alignItems="center" spacing={2} sx={{marginBottom: "20px"}}>
                <Grid size={3}>
                  <Button
                    variant="contained"
                    onClick={() => setValue('selectedGuestType', 2)}
                    fullWidth
                    sx={{
                      backgroundColor: residentGuestCheck === 2 ? "#375e66" : "#e0e0e0",
                      color: residentGuestCheck === 2 ? "#fff" : "#000",
                      fontWeight: "bold",
                      textTransform: "none",
                      borderRadius: "8px",
                      height: "2.5rem",
                      boxShadow: "none",
                      '&:hover': {
                        backgroundColor: residentGuestCheck === 2 ? "#375e66" : "#d0d0d0",
                      }
                    }}
                  >
                    Employee Order
                  </Button>
                </Grid>
                <Grid size={3}>
                  <Button
                    variant="contained"
                    onClick={() => setValue('selectedGuestType', 1)}
                    fullWidth
                    sx={{
                      backgroundColor: residentGuestCheck === 1 ? "#375e66" : "#e0e0e0",
                      color: residentGuestCheck === 1 ? "#fff" : "#000",
                      fontWeight: "bold",
                      textTransform: "none",
                      borderRadius: "8px",
                      height: "2.5rem",
                      boxShadow: "none",
                      '&:hover': {
                        backgroundColor: residentGuestCheck === 1 ? "#375e66" : "#d0d0d0",
                      }
                    }}
                  >
                    Guest Order
                  </Button>
                </Grid>
                <Grid size={3}>
                  <Button
                    variant="contained"
                    onClick={() => setValue('selectedGuestType', 3)}
                    fullWidth
                    sx={{
                      backgroundColor: residentGuestCheck === 3 ? "#375e66" : "#e0e0e0",
                      color: residentGuestCheck === 3 ? "#fff" : "#000",
                      fontWeight: "bold",
                      textTransform: "none",
                      borderRadius: "8px",
                      height: "2.5rem",
                      boxShadow: "none",
                      '&:hover': {
                        backgroundColor: residentGuestCheck === 3 ? "#375e66" : "#d0d0d0",
                      }
                    }}
                  >
                    Other
                  </Button>
                </Grid>
              </Grid>

              {/* Employee View */}
              {residentGuestCheck === 2 && (
                <EmployeeOrder 
                  control={control}
                  errors={errors}
                  register={register}
                  guestBillingTypeId={guestBillingTypeId}
                  chargesByType={chargesByType}
                  facilityId={currentUserData?.facilityId}
                  setValue={setValue}
                  watch={watch}
                />
              )}

              {/* Guest Order View */}
              {residentGuestCheck === 1 && (
                <GuestOrder 
                  control={control}
                  errors={errors}
                  register={register}
                  guestBillingTypeId={guestBillingTypeId}
                  chargesByType={chargesByType}
                  residents={residents}
                />
              )}

              {/* Other View */}
              {residentGuestCheck === 3 && (
                <OtherOrder 
                  control={control}
                  errors={errors}
                  register={register}
                  guestBillingTypeId={guestBillingTypeId}
                  chargesByType={chargesByType}
                />
              )}
            </CardContent>
          </Card>
          <Button
            variant="contained"
            color="primary"
            sx={{margin: "1%", width: "98%"}}
            onClick={handleSubmit(handleGuestSubmit)}
          >
            Take Order
          </Button>
          {open && (
            <InfoModal title={isError ? "Error" : "Alert"} open={open} modalText={modalText} handleClose={handleClose} />
          )}
        </Grid>
      )
    }
    </>
  )
}